import { useFeatureValue } from '@shared/core/featureFlags';
import { useMemo } from 'react';
import { useRouteMatch } from '@react-router';
import { CatalogStorefront } from '@graphql/generated';
import { CATALOG_PATH, CURATIONS_PATH, BABY_SHOP_PATH, SHIPPING_POLICY_PATH, RETURN_POLICY_PATH } from './Catalog.constants';
import { useShopStorefrontContext } from './components/ShopStorefrontProvider';

const PUBLIC_CATALOG_ROUTES = {
  // Wedding shop routes
  curationDetails: `${CATALOG_PATH}${CURATIONS_PATH}/:elementId`,
  curationsIndex: `${CATALOG_PATH}${CURATIONS_PATH}`,
  catalogCategory: `${CATALOG_PATH}/catalog/:elementId`,
  brandPage: `${CATALOG_PATH}/brand/:elementId`,
  catalogIndex: `${CATALOG_PATH}`,
  recommendedProducts: `${CATALOG_PATH}/recommended`,
  brandsList: `${CATALOG_PATH}/brands`,

  // General routes
  catalogItem: `${CATALOG_PATH}/item/:elementId`,

  shippingPolicy: SHIPPING_POLICY_PATH,
  returnPolicy: RETURN_POLICY_PATH,
} as const;

const BABY_CATALOG_ROUTES = {
  curationDetails: `${BABY_SHOP_PATH}${CURATIONS_PATH}/:elementId`,
  curationsIndex: `${BABY_SHOP_PATH}${CURATIONS_PATH}`,
  catalogCategory: `${BABY_SHOP_PATH}/catalog/:elementId`,
  brandPage: `${BABY_SHOP_PATH}/brand/:elementId`,
  catalogIndex: `${BABY_SHOP_PATH}`,
  recommendedProducts: `${BABY_SHOP_PATH}/recommended`,
  brandsList: `${BABY_SHOP_PATH}/brands`,
} as const;

const ADMIN_REGISTRY_ROOT_ROUTE = '/:eventHandle/edit/registry' as const;

type PublicCatalogRoutes = typeof PUBLIC_CATALOG_ROUTES;
type CatalogRouteKey = keyof PublicCatalogRoutes;

type CatalogRoutePaths = {
  [Key in CatalogRouteKey]: {
    paths: [PublicCatalogRoutes[Key], `${typeof ADMIN_REGISTRY_ROOT_ROUTE}${PublicCatalogRoutes[Key]}`];
  };
};

const applyAdminPathPrefix = (publicPath: string) => `${ADMIN_REGISTRY_ROOT_ROUTE}${publicPath}`;

export const useCatalogRoutePaths = () => {
  const { currentStorefront } = useShopStorefrontContext();
  /**
   * Enabling this for soft launch is fine because StorefrontNavigator is conditionally shown based on event type
   * and people will have to directly visit /shop/baby (highly unlikely they'll know about this route)
   */
  const isBabyRegistryEnabled = useFeatureValue('enableBabyRegistrySoftLaunchFeatures');

  const catalogRoutes = useMemo<CatalogRoutePaths>(() => {
    return Object.keys(PUBLIC_CATALOG_ROUTES).reduce((acc, key) => {
      const path = PUBLIC_CATALOG_ROUTES[key as CatalogRouteKey];

      const paths = [path, applyAdminPathPrefix(path)];
      if (isBabyRegistryEnabled) {
        const babyPath = BABY_CATALOG_ROUTES[key as keyof typeof BABY_CATALOG_ROUTES];

        if (babyPath) {
          paths.push(babyPath, applyAdminPathPrefix(babyPath));
        }
      }
      acc[key as CatalogRouteKey] = {
        // Type 'string' is not assignable to type 'never'.
        // @ts-ignore
        paths,
      };
      return acc;
    }, {} as UnsealedReadonly<CatalogRoutePaths>);
  }, [isBabyRegistryEnabled]);

  const adminRouteMatch = useRouteMatch<{ eventHandle: string }>('/:eventHandle/edit/registry');
  const maybeAdminEventHandle = adminRouteMatch?.params.eventHandle;
  const isBabyStorefront = currentStorefront === CatalogStorefront.baby;

  return {
    catalogRoutes,
    isBabyStorefront,
    isAdminRoute: Boolean(maybeAdminEventHandle),
    currentStorefront: currentStorefront,
    shopRootPath: isBabyStorefront
      ? maybeAdminEventHandle
        ? `/${maybeAdminEventHandle}/edit/registry/shop/baby`
        : '/shop/baby'
      : maybeAdminEventHandle
      ? `/${maybeAdminEventHandle}/edit/registry/shop`
      : '/shop',
  };
};
